const defaultFont = '"Open Sans", sans-serif';

/**
 * These styles should exactly match those in the Zapier Design System.
 * That said, this list is not exhaustive for some properties, so not every
 * permutation (e.g. fontWeight) is covered.
 *
 * It is completely acceptable to do something like this in your styles:
 *
 * myTextElement: {
 *   ...Typography.sectionHeader3,
 *   fontWeight: 400,
 * },
 */
export const Typography = {
  // For use cases where styles are unique but the same
  // `fontFamily` should be used.
  base: {
    fontFamily: defaultFont,
  },

  // Page headers
  pageHeader1: {
    fontFamily: defaultFont,
    fontSize: 70,
    lineHeight: '75px',
    fontWeight: 800,
    letterSpacing: -1,
  },
  pageHeader3: {
    fontFamily: defaultFont,
    fontSize: 55,
    lineHeight: '60px',
    fontWeight: 800,
    letterSpacing: -1,
  },
  pageHeader5: {
    fontFamily: defaultFont,
    fontSize: 40,
    lineHeight: '45px',
    fontWeight: 800,
    letterSpacing: -1,
  },
  // Section headers
  sectionHeader1: {
    fontFamily: defaultFont,
    fontSize: 32,
    lineHeight: '35px',
    fontWeight: 700,
    letterSpacing: -1,
  },
  sectionHeader1Extrabold: {
    fontFamily: defaultFont,
    fontSize: 32,
    lineHeight: '35px',
    fontWeight: 800,
    letterSpacing: -1,
  },
  sectionHeader3: {
    fontFamily: defaultFont,
    fontSize: 24,
    lineHeight: '30px',
    fontWeight: 700,
    letterSpacing: -1,
  },
  sectionHeader3Extrabold: {
    fontFamily: defaultFont,
    fontSize: 24,
    lineHeight: '30px',
    fontWeight: 800,
    letterSpacing: -1,
  },
  // Subheaders
  subHeader1: {
    fontFamily: defaultFont,
    fontSize: 24,
    lineHeight: '30px',
    fontWeight: 400,
  },
  subHeader1Semibold: {
    fontFamily: defaultFont,
    fontSize: 24,
    lineHeight: '30px',
    fontWeight: 600,
  },
  subHeader1Bold: {
    fontFamily: defaultFont,
    fontSize: 24,
    lineHeight: '30px',
    fontWeight: 700,
  },
  // Supertitles
  superTitle1: {
    fontFamily: defaultFont,
    fontSize: 14,
    lineHeight: '20px',
    fontWeight: 700,
    textTransform: 'uppercase',
  },
  superTitle3: {
    fontFamily: defaultFont,
    fontSize: 12,
    lineHeight: '15px',
    fontWeight: 700,
    textTransform: 'uppercase',
  },
  superTitle5: {
    fontFamily: defaultFont,
    fontSize: 10,
    lineHeight: '15px',
    fontWeight: 700,
    textTransform: 'uppercase',
  },
  // Paragraphs
  paragraph1: {
    fontFamily: defaultFont,
    fontSize: 18,
    lineHeight: '31px',
    fontWeight: 400,
  },
  paragraph1Bold: {
    fontFamily: defaultFont,
    fontSize: 18,
    lineHeight: '31px',
    fontWeight: 700,
  },
  paragraph1Short: {
    fontFamily: defaultFont,
    fontSize: 18,
    lineHeight: '25px',
    fontWeight: 400,
  },
  paragraph1ShortBold: {
    fontFamily: defaultFont,
    fontSize: 18,
    lineHeight: '25px',
    fontWeight: 700,
  },
  paragraph3: {
    fontFamily: defaultFont,
    fontSize: 16,
    lineHeight: '25px',
    fontWeight: 400,
  },
  paragraph3Semibold: {
    fontFamily: defaultFont,
    fontSize: 16,
    lineHeight: '25px',
    fontWeight: 600,
  },
  paragraph3Bold: {
    fontFamily: defaultFont,
    fontSize: 16,
    lineHeight: '25px',
    fontWeight: 700,
  },
  // Small print
  smallPrint1: {
    fontFamily: defaultFont,
    fontSize: 14,
    lineHeight: '20px',
    fontWeight: 400,
  },
  smallPrint1Semibold: {
    fontFamily: defaultFont,
    fontSize: 14,
    lineHeight: '20px',
    fontWeight: 600,
  },
  smallPrint1Bold: {
    fontFamily: defaultFont,
    fontSize: 14,
    lineHeight: '20px',
    fontWeight: 700,
  },
  smallPrint3: {
    fontFamily: defaultFont,
    fontSize: 12,
    lineHeight: '15px',
    fontWeight: 400,
  },
  smallPrint3Semibold: {
    fontFamily: defaultFont,
    fontSize: 12,
    lineHeight: '15px',
    fontWeight: 600,
  },
  smallPrint3Bold: {
    fontFamily: defaultFont,
    fontSize: 12,
    lineHeight: '15px',
    fontWeight: 700,
  },
} as const;

export type TypographyName = keyof typeof Typography;
